// This module uses the same modules as 'gatsby-transformer-remark' to convert markdown to HTML
import { Parent } from 'hast';
import { fromHtml } from 'hast-util-from-html';
import hastToHTML from 'hast-util-to-html';
import toHAST from 'mdast-util-to-hast';
import Remark from 'remark';
// It is important to use remark-gfm v1.0.0 to reproduce
// the functionality of 'gatsby-transformer-remark'.
import gfm from 'remark-gfm';

export const markdownToHTML = (markdown: string) => {
  const remark = Remark().use(gfm);

  const ast = toHAST(remark.parse(markdown), {
    allowDangerousHtml: true,
  });

  let headings = parseHeadings(ast);

  const html = hastToHTML(ast, {
    allowDangerousHtml: true,
  });

  if (headings.length === 0) {
    // When raw html is returned from Strapi, we need to search the html for headings.
    headings = parseHTMLForHeadings(html);
  }

  return { data: { childMarkdownRemark: { html, headings } } };
};

export const parseHTMLForHeadings = (html: string) => {
  const tree = fromHtml(html);
  // children[0] represents the <html>
  // children[0].children[1] represents the <body>
  const body = (tree.children?.[0] as Parent)?.children?.[1];

  return body ? parseHeadings(body) : [];
};

type Element = { type: string; value?: string };

const parseHeadings = (ast: object) =>
  'children' in ast && Array.isArray(ast.children)
    ? ast.children
        .filter(
          (h) =>
            'tagName' in h &&
            typeof h.tagName === 'string' &&
            h.tagName.match(/^h[1-6]$/) &&
            'children' in h &&
            Array.isArray(h.children),
        )
        .map((h: { tagName: string; children: Element[] }) => ({
          depth: parseInt(h.tagName.substring(1, 2)),
          value: getTextContentRecursive(h.children),
        }))
    : [];

const getTextContentRecursive = (children: Element[]): string =>
  children.reduce(
    (textContent, child) =>
      textContent +
      (child.type === 'text'
        ? (child.value ?? '')
        : 'children' in child && Array.isArray(child.children)
          ? getTextContentRecursive(child.children)
          : ''),
    '',
  );
